import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  GetProducts,
  GetProductsResponse,
  type ProductData,
} from '../../apis/onboarding';

interface RootState {
  Products: ProductState;
}

export interface ProductState {
  productList: ProductData[];
  status: string | number | null;
  is_success: boolean;
  requirement_count?: number;
  hasFetched: boolean;
}

interface ProductRejectedPayload {
  is_success?: boolean;
  status?: number;
  message?: string;
}

export const fetchAllProducts = createAsyncThunk<
  GetProductsResponse['data']['data'],
  { token: string; is_onboarding?: boolean },
  { rejectValue: ProductRejectedPayload }
>('product/GetAllProducts', async ({ token, is_onboarding }, thunkAPI) => {
  try {
    const response = await GetProducts(token, null, null, is_onboarding);
    const data = response?.data;

    if (data?.data?.is_success) {
      return data.data;
    }

    return thunkAPI.rejectWithValue(
      data?.data ?? { message: 'Unknown error occurred' }
    );
  } catch (error) {
    return thunkAPI.rejectWithValue({
      message: error instanceof Error ? error.message : 'Request failed',
    });
  }
});

const productSlice = createSlice({
  name: 'Products',
  initialState: {
    productList: [],
    status: null,
    is_success: true,
    requirement_count: undefined,
    hasFetched: false,
  } as ProductState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllProducts.pending, (state) => {
        state.status = 'loading';
        state.hasFetched = true;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.status = action.payload?.status ?? null;
        state.productList = Array.isArray(action.payload?.data)
          ? action.payload.data
          : [];
        state.is_success = !!action.payload?.is_success;
        state.requirement_count = action.payload?.requirement_count;
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.is_success = false;
        state.status = action.payload?.status ?? 'Unknown error occurred';
        state.productList = [];
        state.hasFetched = false;
      });
  },
});

// selector
export const selectProductState = (state: RootState): ProductState =>
  state.Products;

export const productReducer = productSlice.reducer;
